import React from 'react'
import noop from 'lodash/noop'
import PropTypes from 'prop-types'
import isEqual from 'lodash/isEqual'
import { useMemoDebounced, useHandler, useEffectDebounced, useContentRect } from 'src/hooks'
import { extent, scaleLinear, scaleTime, curveMonotoneX, curveStepAfter } from 'd3'
import { PrintContext, D3Context } from 'src/contexts'
import mapValues from 'lodash/mapValues'
import { useIntl } from 'react-intl'
import { Tooltip } from 'src/components/tooltips'
import isNil from 'lodash/isNil'
import { Curve, Area, Axis, MouseEvents, Brush, Point, Crosshair, Intervals, BoundedArea } from '../common'
import { splitOnZoom, getSample } from './utils'
import {
  pointSymbols,
  defaultPointSymbol,
  xAxisTimeFormat,
  crosshairSymbols,
  defaultCrosshairSymbol,
  timelineTimeFormat,
  crosshairSymbolSize,
} from './options'
import styles from './Chart.scss'

export default function Chart({
  period = [],
  curves = [],
  areas = [],
  zoom = [],
  onZoomChange = noop,
  intervals = [],
  tooltipContent: TooltipContent,
  withVerticalZoom = false,
  showGrid = true,
  minZoomDuration = 1000 * 60 * 60,
  contextHeight = 50,
  contextOffset = 10,
  axisOffset = 10,
  axisHeight = 20,
  axisWidth = 40,
  debounceDuration = 100,
  transitionDuration = 500,
}) {
  /**
   * Retrieve intl
   */
  const intl = useIntl()

  /**
   *
   *  Initialization local states
   *
   */

  // local zoom state
  const [zoomX, setZoomX] = React.useState(zoom)
  // current cursor position
  const [cursorPosition, setCursorPosition] = React.useState({})

  /**
   *
   *  Initialize refs
   *
   */

  // ref used to prevent animation on previous zoom
  const lastEmittedZoom = React.useRef([])
  // ref used to store current animation timeout
  const animationTimeout = React.useRef()
  // get printable ref for svg root node
  const { ref: nodeRef } = PrintContext.use()
  // accessor of the current transition duration
  const getTransitionDuration = useHandler(() => (animationTimeout.current ? transitionDuration : 0))

  /**
   *
   *  Compute derived data
   *
   */

  // safely update zoom state with or without animation
  const updateZoomX = useHandler((zoom, animate) => {
    setCursorPosition()
    clearTimeout(animationTimeout.current)
    animationTimeout.current = animate ? setTimeout(() => (animationTimeout.current = null), transitionDuration) : null
    setZoomX([...zoom])
  })

  // get all different units
  const units = React.useMemo(() => [...new Set(curves.map(({ unit }) => unit))], [curves])

  // get chart dimensions
  const { width = 400, height = 300, top, left } = useContentRect(nodeRef)
  const chartWidth = width - units.length * axisWidth - axisOffset
  const chartHeight = height - contextHeight - contextOffset - axisHeight - axisOffset
  const leftWidth = axisWidth * units.length + axisOffset
  const yTicks = chartHeight / 50
  const xTicks = chartWidth / 100
  const timelineTicks = chartWidth / 200
  const halfWidth = Math.floor(chartWidth / 2)

  // compute time and zoom related data
  const [from, to] = period
  const diff = to - from
  const minZoom = diff / minZoomDuration

  /**
   *
   *  Effects
   *
   */

  /**
   * Trigger zoom animation on zoom prop change
   * (Hack : use useMemo instead of useEffect because useEffect happens too late)
   */
  React.useMemo(
    () => {
      if (!isEqual(zoom, lastEmittedZoom.current)) {
        lastEmittedZoom.current = null
        updateZoomX(zoom, true)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [zoom[0], zoom[1]]
  )

  /**
   * Trigger zoom animation when enabling or disabling vertical zoom
   * (Hack : use useMemo instead of useEffect because useEffect happens too late)
   */
  React.useMemo(
    () => {
      updateZoomX(zoomX, true)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [withVerticalZoom, period, curves, leftWidth, chartHeight, chartWidth]
  )

  /**
   * Call onZoomChange when zoom state changes
   */
  useEffectDebounced(debounceDuration)(() => {
    if (!isEqual(zoom, zoomX)) {
      lastEmittedZoom.current = zoomX
      onZoomChange(zoomX)
    }
  }, [zoomX[0], zoomX[1]])

  /**
   *
   *  Memoized values
   *
   */

  /**
   * Compute data samples for timeline area
   */
  const timelineSamples = React.useMemo(
    () => curves.reduce((result, { id, data }) => ({ ...result, [id]: getSample(data, halfWidth) }), {}),
    [curves, halfWidth]
  )

  /**
   * Compute data samples and visible points after a delay
   */
  const chartSamples = useMemoDebounced(debounceDuration + getTransitionDuration())(
    () =>
      curves.reduce((result, { id, data }) => {
        const [before, visible, after] = splitOnZoom(data, zoomX)
        const pointsBefore = getSample(before, (zoomX[0] - from) * (halfWidth / diff))
        const pointsVisible = getSample(visible, halfWidth)
        const pointsAfter = getSample(after, (to - zoomX[1]) * (halfWidth / diff))

        return {
          ...result,
          [id]: {
            data: [...pointsBefore, ...pointsVisible, ...pointsAfter],
            points: pointsVisible.length < Math.floor(chartWidth / 10) ? pointsVisible : [],
          },
        }
      }, {}),
    [zoomX[0], zoomX[1], curves, chartWidth, halfWidth, from, to, diff]
  )

  /**
   * Create a dictionnary of extents, by id
   */
  const extentsById = React.useMemo(() => mapValues(timelineSamples, data => extent(data, d => d.value)), [
    timelineSamples,
  ])

  /**
   * Create a dictionnary of extents, by unit
   */
  const extentsByUnit = React.useMemo(
    () =>
      curves.reduce(
        (result, curve) => ({
          ...result,
          [curve.unit]: extent([...(result[curve.unit] || []), ...(extentsById[curve.id] || [])]),
        }),
        {}
      ),
    [curves, extentsById]
  )

  /**
   * Create timeline scales
   */
  const timelineScales = React.useMemo(() => {
    const yZoomByUnit = !withVerticalZoom
      ? extentsByUnit
      : curves.reduce(
          (result, { unit, id, hidable }) => ({
            ...result,
            [unit]: extent([...(result[unit] || []), ...(!hidable ? extentsById[id] || [] : [])]),
          }),
          {}
        )

    return {
      x: scaleTime()
        .clamp(true)
        .range([0, chartWidth])
        .domain(period),
      y: mapValues(yZoomByUnit, ([min, max]) => {
        const space = (max - min) / 5
        return scaleLinear()
          .range([contextHeight, 0])
          .domain([min - space, max + space])
      }),
    }
  }, [chartWidth, period, extentsByUnit, contextHeight, curves, withVerticalZoom, extentsById])

  /**
   * Create chart scales
   */
  const chartScales = React.useMemo(
    () => {
      const yZoomByUnit = !withVerticalZoom
        ? extentsByUnit
        : curves.reduce(
            (result, { unit, id, hidable }) => ({
              ...result,
              [unit]: (() => {
                const { data } = chartSamples[id] || {}
                if (hidable || !data) {
                  return result[unit] || []
                } else {
                  const [before, visible, after] = splitOnZoom(data, zoomX).map(data => data.map(({ value }) => value))
                  return extent([[...before].pop(), after[0], ...visible, ...(result[unit] || [])])
                }
              })(),
            }),
            {}
          )

      // create the normal X scale
      const scale = scaleTime()
        .range([0, chartWidth])
        .domain(zoomX)

      // create a custom clamped version of it (clamped to `[-chartWidth, 2*chartWidth]` instead of `[0, chartWidth]`)
      const clamped = d => Math.max(-chartWidth, Math.min(2 * chartWidth, scale(d)))

      // and provide it the original scale prototype
      Object.setPrototypeOf(clamped, scale)

      return {
        x: clamped,
        y: mapValues(yZoomByUnit, ([min, max]) => {
          const space = (max - min) / 5
          return scaleLinear()
            .range([chartHeight, 0])
            .domain([min - space, max + space])
        }),
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartWidth, chartHeight, zoomX, curves, chartSamples]
  )

  /**
   * Compute current zoom extent
   */
  const zoomExtent = React.useMemo(() => zoomX.map(timelineScales.x), [zoomX, timelineScales.x])

  /**
   * Compute chart point pixel positions
   */
  const chartScaledCurves = React.useMemo(
    () =>
      curves.map(curve => {
        const { data = [], points = [] } = chartSamples[curve.id] || {}
        return {
          ...curve,
          curveFactory: curve.squared ? curveStepAfter : curveMonotoneX,
          data: data.map(d => ({
            x: chartScales.x(d.date),
            y: isNil(d.value) ? null : chartScales.y[curve.unit](d.value),
            d,
          })),
          points: points
            .filter(d => !isNil(d.value))
            .map(d => ({
              x: chartScales.x(d.date),
              y: chartScales.y[curve.unit](d.value),
              d,
            })),
        }
      }),
    [chartScales, curves, chartSamples]
  )

  /**
   * Compute timeline point pixel positions
   */
  const timelineScaledCurves = React.useMemo(
    () =>
      curves.map(curve => ({
        ...curve,
        curveFactory: curve.squared ? curveStepAfter : curveMonotoneX,
        data: (timelineSamples[curve.id] || []).map(d => ({
          x: timelineScales.x(d.date),
          y: isNil(d.value) ? null : timelineScales.y[curve.unit](d.value),
          d,
        })),
      })),
    [timelineScales, curves, timelineSamples]
  )

  /**
   * Compute currently highlighted point
   */
  const highlightedPoint = React.useMemo(() => {
    return (
      cursorPosition &&
      chartScaledCurves.reduce((closest, curve) => {
        const { data, hidable } = curve
        return !hidable
          ? data.reduce((closest, point) => {
              const distance = Math.sqrt(
                Math.pow(cursorPosition[0] - point.x, 2) + Math.pow(cursorPosition[1] - point.y, 2)
              )
              return !closest || distance < closest.distance ? { distance, ...point, curve } : closest
            }, closest)
          : closest
      }, undefined)
    )
  }, [chartScaledCurves, cursorPosition])

  /**
   * Compute ClientRect related to currently highlighted point
   */
  const highlightedPointRect = React.useMemo(() => {
    const sideLength = Math.floor(Math.sqrt(crosshairSymbolSize))
    return (
      highlightedPoint && {
        top: top + highlightedPoint.y - sideLength / 2,
        left: left + units.length * axisWidth + axisOffset + highlightedPoint.x - sideLength / 2,
        width: sideLength,
        height: sideLength,
        get right() {
          return this.left + this.width
        },
        get bottom() {
          return this.top + this.height
        },
      }
    )
  }, [highlightedPoint, units, top, left, axisWidth, axisOffset])

  /**
   * Collect the curves associated to areas
   */
  const chartScaledAreas = React.useMemo(
    () =>
      [...new Set(areas.flatMap(area => [area.from, area.min, area.max]))]
        .map(id => chartScaledCurves.find(curve => curve.id === id))
        .filter(Boolean),
    [areas, chartScaledCurves]
  )

  /**
   *
   * Callbacks
   *
   */

  /**
   * Zoom handler for MouseEvents component
   * @see https://github.com/d3/d3-zoom#transform_rescaleX
   */
  const onZoom = useHandler(transform => {
    const copy = chartScales.x.copy().domain(period)

    updateZoomX(
      copy
        .range()
        .map(d => transform.invertX(d))
        .map(d => copy.invert(d))
        // round to the second
        .map(d => new Date(Math.round(d.getTime() / 1000) * 1000))
    )
  })

  /**
   * Brush handler for Brush components
   */
  const onBrush = useHandler(extent => {
    updateZoomX(
      extent
        .map(d => timelineScales.x.invert(d))
        // round to the second
        .map(d => new Date(Math.round(d.getTime() / 1000) * 1000))
    )
  })

  /**
   *
   * Chart template
   *
   */

  return (
    <div ref={nodeRef} className={styles.container}>
      <D3Context.Provider getTransitionDuration={getTransitionDuration}>
        <svg className={styles.graph}>
          {/* Definitions for areas and masks */}
          <defs>
            {chartScaledAreas.map(({ id, data, curveFactory }) => (
              <React.Fragment key={`${id}-areas`}>
                <Area id={`${id}-below`} curveFactory={curveFactory} data={data} y0={height} />
                <Area id={`${id}-above`} curveFactory={curveFactory} data={data} y0={0} />
              </React.Fragment>
            ))}
          </defs>

          <svg className={styles.chartContainer} height={chartHeight + axisHeight} width={chartWidth + leftWidth}>
            <g transform={`translate(${leftWidth},0)`} className={styles.transition}>
              <svg width={chartWidth} height={chartHeight}>
                {/* Areas between curves */}
                {areas.map(area => (
                  <BoundedArea key={area.id} {...area} />
                ))}

                {/* Curves and points */}
                {chartScaledCurves.map(({ id, dashed, color, data, curveFactory, points, hidable }) => (
                  <g key={id}>
                    <Curve color={color} dashed={dashed} curveFactory={curveFactory} data={data} />
                    {!hidable &&
                      points.map(point => (
                        <Point
                          key={`${id}-${point.d.date}`}
                          point={point}
                          symbol={pointSymbols[point.d.origin] || defaultPointSymbol}
                          color={color}
                        />
                      ))}
                  </g>
                ))}
              </svg>

              {/* Intervals under the chart */}
              <svg width={chartWidth}>
                <Intervals data={intervals} scale={chartScales.x} yOffset={chartHeight - 3} />
              </svg>

              {/* Crosshair for the hovered point */}
              {highlightedPoint && (
                <Crosshair
                  x={highlightedPoint.x}
                  y={highlightedPoint.y}
                  color={highlightedPoint.curve.color}
                  symbol={crosshairSymbols[highlightedPoint.d.origin] || defaultCrosshairSymbol}
                  xMin={-axisOffset}
                  xMax={chartWidth}
                  yMin={0}
                  yMax={chartHeight + axisOffset}
                />
              )}

              {/* Y grid */}
              <Axis
                scale={chartScales.y[units[0]]}
                direction={'left'}
                ticks={yTicks}
                tickSizeInner={showGrid ? -chartWidth : 0}
                tickSizeOuter={0}
                format={''}
                className={styles.grid}
              />

              {/* X grid */}
              <Axis
                transform={`translate(0, ${chartHeight})`}
                className={styles.grid}
                scale={chartScales.x}
                direction={'bottom'}
                ticks={xTicks}
                tickSizeInner={showGrid ? -chartHeight : 0}
                tickSizeOuter={0}
                format={''}
              />

              {/* X axis */}
              <Axis
                transform={`translate(0, ${chartHeight + axisOffset})`}
                className={styles.transition}
                scale={chartScales.x}
                direction={'bottom'}
                ticks={xTicks}
                format={React.useMemo(() => xAxisTimeFormat(intl), [intl])}
              />

              {/* Y axis */}
              {units.map((unit, index) => (
                <Axis
                  key={unit}
                  className={styles.transition}
                  unit={unit}
                  height={chartHeight}
                  transform={`translate(-${axisOffset + axisWidth * index}, 0)`}
                  scale={chartScales.y[unit]}
                  direction={'left'}
                  ticks={yTicks}
                ></Axis>
              ))}

              {/* Zoom and mousemove events */}
              <MouseEvents
                onZoom={onZoom}
                value={zoomX}
                scale={chartScales.x}
                boundaries={period}
                onZoomEnd={setCursorPosition}
                onMousemove={setCursorPosition}
                onMouseout={setCursorPosition}
                onBlur={setCursorPosition}
                width={chartWidth}
                height={chartHeight}
                minZoom={minZoom}
                extent={zoomExtent}
              />
            </g>
          </svg>

          <g
            className={styles.timeline}
            transform={`translate(${leftWidth}, ${chartHeight + contextOffset + axisHeight + axisOffset})`}
          >
            <svg>
              {/* Areas in timeline */}
              {timelineScaledCurves.map(
                ({ id, curveFactory, data, color, hidable }) =>
                  !hidable && (
                    <Area
                      key={id}
                      id={id}
                      data={data}
                      y1={contextHeight}
                      curveFactory={curveFactory}
                      fill={color}
                      opacity={0.5}
                    />
                  )
              )}

              {/* Intervals under timeline */}
              <Intervals data={intervals} scale={timelineScales.x} yOffset={contextHeight + 3} />

              {/* Timeline axis */}
              <Axis
                transform={`translate(0, ${contextHeight})`}
                className={styles.timelineAxis}
                scale={timelineScales.x}
                direction={'top'}
                ticks={timelineTicks}
                format={React.useMemo(() => timelineTimeFormat(intl), [intl])}
              />

              {/* Brush handle */}
              <Brush onBrush={onBrush} width={chartWidth} height={contextHeight} extent={zoomExtent} />
            </svg>
          </g>
        </svg>

        {/* Information about the currently hovered point */}
        {highlightedPointRect && TooltipContent && (
          <Tooltip
            visible
            popperProps={{ placement: 'top' }}
            referenceElement={{
              getBoundingClientRect: () => highlightedPointRect,
              clientWidth: highlightedPointRect.width,
              clientHeight: highlightedPointRect.height,
            }}
            title={<TooltipContent point={highlightedPoint.d} curve={highlightedPoint.curve} />}
          />
        )}
      </D3Context.Provider>
    </div>
  )
}

Chart.propTypes = {
  zoom: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  period: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  curves: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      unit: PropTypes.string.isRequired,
      color: PropTypes.string,
      dashed: PropTypes.bool,
      squared: PropTypes.bool,
      data: PropTypes.array,
      hidable: PropTypes.bool,
    })
  ),
  areas: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      color: PropTypes.string,
      from: PropTypes.string,
      isBelow: PropTypes.bool,
      min: PropTypes.string,
      max: PropTypes.string,
    })
  ),
  onZoomChange: PropTypes.func,
  intervals: PropTypes.arrayOf(
    PropTypes.shape({
      startDate: PropTypes.instanceOf(Date),
      endDate: PropTypes.instanceOf(Date),
      color: PropTypes.string,
    })
  ),
  tooltipContent: PropTypes.elementType,
  withVerticalZoom: PropTypes.bool,
  showGrid: PropTypes.bool,
  minZoomDuration: PropTypes.number,
  contextHeight: PropTypes.number,
  contextOffset: PropTypes.number,
  axisOffset: PropTypes.number,
  axisHeight: PropTypes.number,
  axisWidth: PropTypes.number,
  debounceDuration: PropTypes.number,
  transitionDuration: PropTypes.number,
}
